// ET (Eddy Current Testing) Types for Inspector App

import { matchesCategoryFamily } from '@/utils/categoryFamily'

// ET Object Types
export type ETObjectType = 'POU' | 'POU w/ISO' | 'Sling' | 'custom'

// ET Status Types
export type ETStatus = 'Accepted' | 'Not Accepted'

// ET Material Type (only Carbon steel for ET)
export type ETMaterialType = 'Carbon steel'

export const ET_TEST_SITES = ['Bergen', 'Mongstad', 'Ågotnes'] as const
export type ETTestSite = (typeof ET_TEST_SITES)[number]
export const ET_DEFAULT_TEST_SITE: ETTestSite = 'Bergen'

export function inferETObjectType(
  categoryCode: string | null | undefined,
  name: string,
): { objectType: ETObjectType; customObjectType?: string; isChainSling: boolean } {
  const code = (categoryCode || '').toUpperCase()
  if (matchesCategoryFamily(code, 'R1')) {
    return { objectType: 'Sling', customObjectType: undefined, isChainSling: true }
  }
  if (matchesCategoryFamily(code, 'R3')) {
    return { objectType: 'Sling', customObjectType: undefined, isChainSling: false }
  }
  if (code.startsWith('F') && !code.startsWith('FAL')) {
    const objectType: ETObjectType = name.toUpperCase().includes('ISO') ? 'POU w/ISO' : 'POU'
    return { objectType, customObjectType: undefined, isChainSling: false }
  }
  return { objectType: 'custom', customObjectType: name, isChainSling: false }
}

// Probe Settings for ET Testing
export interface ETProbeSettings {
  type: string
  frequency: string
  gain: string
  phase: string
  enforcement: string
}

// Image with comment interface
export interface ETImageWithComment {
  id: string
  image: string // Base64 encoded image
  comment?: string
}

// ET Form Data
export interface ETFormData {
  testDate: string
  testSite: string
  reportNumber: string
  unitId: string
  slingId?: string
  isChainSling: boolean
  issuedBy: string
  status: ETStatus
  objectType: ETObjectType
  customObjectType?: string
  material: ETMaterialType
  company?: string
  company_id?: string
  report_code?: string | null
  comments?: string
  attachedImages: ETImageWithComment[]
  verifiedBy?: string
}

// ET Report Data for PDF generation
export interface ETReportData {
  testDate: string
  testSite?: string
  reportNumber: string
  unitId: string
  slingId?: string
  isChainSling: boolean
  issuedBy: string
  verifiedBy?: string
  status: ETStatus
  objectType: ETObjectType
  customObjectType?: string
  probeSettings: ETProbeSettings[]
  certificationNumber: string
  sequenceNumber: string
  material: ETMaterialType
  company?: string
  report_code?: string | null
  comments?: string
  attachedImages: ETImageWithComment[]
  approvedBy?: string
  approvedAt?: string
}

// Inspector interface
export interface ETInspector {
  id: string
  full_name: string
  certificate_no?: string
  signature?: string
  et_level?: number
  et_equipment?: string
  role?: string
}

// Company interface
export interface ETCompany {
  id: string
  name: string
  report_code: string | null
}

// Default probe settings
export const DEFAULT_ET_PROBE_SETTINGS: ETProbeSettings[] = [
  { type: 'Absolute', frequency: '500', gain: '21', phase: '120', enforcement: '0' },
  { type: 'Bridge', frequency: '100', gain: '55', phase: '190', enforcement: '0' }
]

// ET Equipment and reference standards
export const ET_EQUIPMENT = {
  instrument: 'GE Phasec 3',
  serialNumber: '801C4RP',
  referenceBlock: 'Carbon steel 0,5 mm - 1,0 mm - 2,0 mm',
  referenceSerial: 'CZ1041/14'
} as const

// ET Test Standards
export const ET_STANDARDS = [
  'NDT ET DNVGL-CG-0051',
  'ISO 17643',
  'ASTM E309',
  'EN 1711'
] as const

